import { useTranslation } from "react-i18next";
import { PlatformRole } from "~/config/types";
import { useLanguageContext } from "~/context/language";
import { useTheme } from "~/context/theme";
import { useAuthContext } from "~/hooks/useAuthContext";
import { useCanManageGovernanceUnits } from "~/hooks/useCanManageGovernanceUnits";
import { useUserContext } from "~/hooks/useUserContext";
import { deleteSession } from "~/api/v2/requests";
import {
  getSelectedGovernanceUnitId,
  setSelectedGovernanceUnitId,
} from "~/helpers/governanceUnit";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { Button } from "~/atoms/button";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import { printFullName } from "~/helpers/user";
import {
  Languages,
  LogOut,
  Moon,
  Network,
  Settings,
  Sun,
  Users,
  HelpCircle,
} from "lucide-react";
import { languages, getLangLabels } from "~/i18n";
import { Link } from "react-router";
import { type MouseEvent } from "react";
import { usePostHog } from "posthog-js/react";

const UserNav = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const langLabel = getLangLabels(t);
  const { language, setLanguage } = useLanguageContext();
  const { theme, setTheme } = useTheme();
  const { setUser } = useAuthContext();
  const posthog = usePostHog();
  const showGovernanceUnits = useCanManageGovernanceUnits();

  const isReader = user.role === PlatformRole.reader;

  const signOut = async () => {
    await deleteSession();
    setUser(undefined);
    posthog.reset();
  };

  // Account settings concern the home unit. Acting in another unit, switch
  // back: a full navigation drops everything fetched for the acting unit, like
  // the switcher does.
  const openSettings = (event: MouseEvent) => {
    if (getSelectedGovernanceUnitId() !== null) {
      event.preventDefault();
      setSelectedGovernanceUnitId(null);
      window.location.assign("/account-settings");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative size-10 rounded-full">
          <AvatarWithFallback user={user} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm leading-none font-medium">
              {printFullName(user)}
            </p>
            <p className="text-muted-foreground text-xs leading-none">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <Link to="/account-settings" onClick={openSettings}>
            <DropdownMenuItem>
              <Settings />
              {t(($) => $.settings.header)}
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Languages />
              {t(($) => $.commons.language)}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={language}
                  onValueChange={setLanguage}
                >
                  {Object.values(languages).map((lang) => (
                    <DropdownMenuRadioItem key={lang.code} value={lang.code}>
                      {langLabel[lang.code]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              {theme === "dark" ? <Moon /> : <Sun />}
              {t(($) => $.commons.theme)}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={theme}
                  onValueChange={(v) => setTheme(v as "light" | "dark")}
                >
                  <DropdownMenuRadioItem value="light">
                    <Sun />
                    {t(($) => $.commons.themeLight)}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">
                    <Moon />
                    {t(($) => $.commons.themeDark)}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <Link to="/support">
            <DropdownMenuItem>
              <HelpCircle />
              {t(($) => $.commons.help)}
            </DropdownMenuItem>
          </Link>
        </DropdownMenuGroup>
        {!isReader && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                {t(($) => $.commons.yourOrganisation)}
              </DropdownMenuLabel>
              <Link to="/users">
                <DropdownMenuItem>
                  <Users />
                  {t(($) => $.commons.users)}
                </DropdownMenuItem>
              </Link>
              {showGovernanceUnits && (
                <Link to="/governance-units">
                  <DropdownMenuItem>
                    <Network />
                    {t(($) => $.governanceUnits.title)}
                  </DropdownMenuItem>
                </Link>
              )}
            </DropdownMenuGroup>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut}>
          <LogOut />
          {t(($) => $.commons.signOut)}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserNav;
